import React, { ReactNode, createContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { emptyAddress } from './constants'
import { FormattedAddress } from './types'
import { formattedAddress } from './utils'

export enum ContextAddressType {
  unverified = 'unverified',
  corrected = 'corrected',
}

type Addresses = {
  [key in ContextAddressType]?: FormattedAddress
}

type SetAddressState = (type: ContextAddressType, address: FormattedAddress) => void

export interface AddressContextProps {
  addresses: Addresses
  setAddress: SetAddressState
}

interface AddressProviderProps {
  children?: ReactNode
}

export const AddressContext = createContext<AddressContextProps>(null)
export const AddressProvider = ({ children }: AddressProviderProps) => {
  const location = useLocation<{
    addressData: FormattedAddress
    addShippingAddressState: boolean
  }>()
  const addressData = location.state?.addressData
  const addShippingAddressState = location.state?.addShippingAddressState
  const [corrected, setCorrectedAddress] = useState<FormattedAddress>(emptyAddress)
  const [unverified, setUnverifiedAddress] = useState<FormattedAddress>(emptyAddress)

  /**
   * @description Sets the useState hooks of associate address based on ContextAddressType specified
   * @param type For each key in ContextAddressType, the associated address has a shape of FormattedAddress
   * @param address Address that will be set to provided ContextAddressType
   */
  const setAddress: SetAddressState = (type, address) => {
    switch (type) {
      case ContextAddressType.unverified:
        setUnverifiedAddress(address)
        break
      case ContextAddressType.corrected:
        setCorrectedAddress(address)
        break
    }
  }

  /**
   * @description Updates ContextAddressType.unverified whenever addressData is updated in the browsers history
   * Can set the unverified address to null if addShippingAddressState is true
   */
  useEffect(() => {
    if (addressData || addShippingAddressState) {
      setAddress(
        ContextAddressType.unverified,
        formattedAddress(addShippingAddressState ? null : addressData),
      )
    }
  }, [addressData, addShippingAddressState])

  return (
    <AddressContext.Provider
      value={{
        addresses: {
          unverified: unverified,
          corrected: corrected,
        },
        setAddress,
      }}
    >
      {children}
    </AddressContext.Provider>
  )
}
